import React from 'react';
import { Navigate } from 'react-router-dom';
import { useUserState } from '../features/user/operations/UserStateProvider';
import { UserRole } from '@/shared/types';
import { ROUTES } from '@/shared/services/constants';
import Loading from '@/shared/components/ui/Loading';
import { ENV } from '@/config/environment';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  /** Entitlement page key - denied users are redirected same as a role failure.
   * This is UX only; the real enforcement is the backend's page_entitlement
   * decorator on whatever endpoints this page calls. */
  requiredPageKey?: string;
}

/**
 * Component that protects routes requiring authentication or specific roles
 * - Protected routes redirect to login if not authenticated
 * - Admin routes redirect to home if not admin (instead of showing forbidden)
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles,
  requiredPageKey,
}) => {
  const { isAuthenticated, loading: isLoading, hasRole, hasEntitlement } = useUserState();

  // Local development can unlock every frontend route without real auth.
  if (ENV.app.localDevMode) {
    return <>{children}</>;
  }

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loading size="lg" text="Verifying authentication..." />
      </div>
    );
  }

  // A requiredPageKey at 'public' tier passes hasEntitlement() even for a
  // logged-out visitor (see UserStateProvider) - check it before the blanket
  // auth redirect below, so a page an admin has opened up to everyone
  // actually renders for everyone, not just people who happen to be logged
  // in already.
  const pageKeyGrantsPublicAccess = !!requiredPageKey && hasEntitlement(requiredPageKey);

  // If not authenticated, redirect to login - unless this specific page's
  // tier already let it through above.
  if (!isAuthenticated && !pageKeyGrantsPublicAccess) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  // If roles are required, check if user has permission
  if (requiredRoles && requiredRoles.length > 0) {
    const hasRequiredRole = requiredRoles.some(role => hasRole(role));

    if (!hasRequiredRole) {
      // For admin routes, redirect to home instead of showing forbidden
      return <Navigate to={ROUTES.HOME} replace />;
    }
  }

  // Per-user entitlement deny, or a tier this user's role/grants don't
  // satisfy - same redirect as a role failure, since both mean "this page
  // isn't for you," not "you're not logged in."
  if (requiredPageKey && !hasEntitlement(requiredPageKey)) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  // User is authenticated and authorized, render the protected component
  return <>{children}</>;
};

export default ProtectedRoute;